import { fromJS, List } from 'immutable'
import { ACTIVE, MODULE_TYPE } from 'utils/constants'
import { compose } from 'ramda'
import {
  updateIn,
  removeAllIn,
  findKeysBy,
  pickToArray,
  pluck,
  flattenList,
  filterNot,
} from 'utils/immutable'
import {
  COMMIT_UPDATE_FEATURE_STATUS,
  PRE_CREATE_NEW_FEATURE,
  RESET_PRE_CREATE_NEW_FEATURE,
  SET_IS_PRE_CREATE_NEW_FEATURE,
  SET_IS_FEATURE_MODAL_VIEW,
  SET_FEATURE_IS_DIRTY,
} from 'actions/feature/features'

import {
  DEFAULT_ID,
  DEFAULT_STATUS,
  DEFAULT_TYPE,
} from 'constants/featureDefaults'

import { createReducer } from 'reducers/utils'
import affinityGroupHandlers from 'reducers/feature/affinityGroups'
import featureHandlers from 'reducers/feature/features'
import featureMetadataHandlers from 'reducers/feature/featureMetadata'
import functionHandlers from 'reducers/feature/functions'
import inputHandlers from 'reducers/feature/inputs'
import logicNodeHandlers from 'reducers/feature/logicNodes'
import outputHandlers from 'reducers/feature/outputs'
import queryHandlers from 'reducers/feature/queries'
import conditionHandlers from 'reducers/feature/conditions'
import noteHandlers from 'reducers/feature/notes'
import snapshotHandlers from 'reducers/feature/snapshots'
import harnessesHandlers from 'reducers/feature/harnesses'
import simulationHandlers from 'reducers/feature/simulation'

export const initialFunction = {
  queries: [],
  uri: 'tmp_func',
  id: 'tmp_func',
  outputMap: '',
  description: '',
}

export const initialFeatureMetaData = {
  priority: 50,
  releaseNumber: '',
  subGroup: '',
  successorCode: '',
}

const initialFeature = fromJS({
  initial: {
    status: DEFAULT_STATUS,
    moduleType: '',
    moduleName: '',
    type: DEFAULT_TYPE,
    subGroup: '',
    successorCode: '',
    description: '',
    vehicleSubType: '',
    id: DEFAULT_ID,
  },
})

export const initialState = fromJS({
  originalFeature: null,
  currentFeature: {
    isPrecreate: false,
    isModalView: false,
    outputs: {},
    outputMaps: {},
    queries: {},
    functions: {
      [initialFunction.uri]: initialFunction,
    },
    feature: initialFeature,
  },
  affinityGroups: {},
  simulations: {},
  signals: {},
  outputPins: {},
  notes: [],
  snapshots: {},
  harnesses: {
    modules: {},
    harnesses: {},
  },
  isDirty: false,
})

const handlers = {
  ...affinityGroupHandlers,
  ...featureHandlers,
  ...featureMetadataHandlers,
  ...functionHandlers,
  ...inputHandlers,
  ...logicNodeHandlers,
  ...outputHandlers,
  ...queryHandlers, // TODO: delete?
  ...conditionHandlers,
  ...noteHandlers,
  ...snapshotHandlers,
  ...harnessesHandlers,
  ...simulationHandlers,
  [COMMIT_UPDATE_FEATURE_STATUS]: updateFeatureStatus,
  [PRE_CREATE_NEW_FEATURE]: preCreateFeature,
  [RESET_PRE_CREATE_NEW_FEATURE]: resetPreCreateNewFeature,
  [SET_IS_PRE_CREATE_NEW_FEATURE]: setIsPreCreateNewFeature,
  [SET_IS_FEATURE_MODAL_VIEW]: setIsModalViewForFeature,
  [SET_FEATURE_IS_DIRTY]: (state, { isDirty }) => state.set('isDirty', isDirty),
}

export default createReducer(initialState, handlers)

function preCreateFeature(state, { moduleName }) {
  const moduleType = MODULE_TYPE[moduleName]

  return state.mergeIn(['currentFeature', 'feature', 'initial'], {
    moduleName,
    moduleType,
  })
}

function resetPreCreateNewFeature(state) {
  return state.setIn(['currentFeature', 'feature'], initialFeature)
}

function setIsPreCreateNewFeature(state) {
  const isPrecreate = state.getIn(['currentFeature', 'isPrecreate'])
  return state.setIn(['currentFeature', 'isPrecreate'], !isPrecreate)
}

function setIsModalViewForFeature(state) {
  const isModalView = state.getIn(['currentFeature', 'isModalView'])
  return state.setIn(['currentFeature', 'isModalView'], !isModalView)
}

// TODO: add some helpers for the removals and place them in a file
// there's a lot of overlap with deletion of functions in 'reducers/feature/functions'
function updateFeatureStatus(
  state,
  { status, previousStatus, orphanQueryUris = [] }
) {
  const featureId = state.getIn(['currentFeature', 'feature']).first().get('id')

  // if feature is not being set to active, we don't need to remove anything
  // but we do need to capture status changes
  if (status !== ACTIVE) {
    // we only deal with one feature at a time, don't have to set currentFeature, feature, key/value
    // plus, currentFeature, feature, gets reset on update change
    return state.setIn(['previousStatus'], previousStatus)
  }

  // need to remove all orphans/orphan-like content
  // this includes any content in functions w/o an output query
  const allQueries = state.getIn(['currentFeature', 'queries'])
  const outputMaps = state.getIn(['currentFeature', 'outputMaps'])

  const getConditions = compose(
    flattenList,
    pluck('conditions'),
    pickToArray(orphanQueryUris)
  )

  const conditionUrisToRemove = getConditions(allQueries)

  const removeOrphanConditions = removeAllIn(conditionUrisToRemove, [
    'currentFeature',
    'conditions',
  ])
  const removeOrphanQueries = removeAllIn(orphanQueryUris, [
    'currentFeature',
    'queries',
  ])
  const removeOrphanQueriesInFeatureList = updateIn(
    ['currentFeature', 'feature', featureId, 'queries'],
    filterNot((item) => orphanQueryUris.includes(item.get('id')))
  )

  // function ids without outputQueries
  const functionsToRemove = findKeysBy((outputMap) => {
    const outputQuery = outputMap.get('outputQuery')

    return !outputQuery || outputQuery === ''
  }, outputMaps).toList()

  // output ids we want to remove
  // these are outputs of functions without an outputQuery
  const outputIdsToRemove = functionsToRemove.reduce((acc, fnId) => {
    const outputs = outputMaps.getIn([fnId, 'outputs'])
    return acc.concat(outputs)
  }, List())

  // remove from outputs Map
  const removeOutputs = removeAllIn(outputIdsToRemove, [
    'currentFeature',
    'outputs',
  ])

  // remove from outputMaps Map
  const removeMappedOutputs = removeAllIn(functionsToRemove, [
    'currentFeature',
    'outputMaps',
  ])

  // remove from Functions list in feature
  const removeFunctionsFromFeature = updateIn(
    ['currentFeature', 'feature', featureId, 'functions'],
    (functions) => functions.filter((f) => !functionsToRemove.includes(f))
  )

  const removePreviousStatus = (state) => state.set('previousStatus', '')

  return compose(
    removeOutputs,
    removeMappedOutputs,
    removeFunctionsFromFeature,
    removeOrphanQueriesInFeatureList,
    removeOrphanConditions,
    removeOrphanQueries,
    removePreviousStatus
  )(state)
}
